import { createStore } from '@shared/utils/state/state-factory'
import { cloneDeep, merge } from 'lodash'
import type { Channels } from 'v2/types/api/conversations'
import { isUUID } from 'v2/utils/uuid'
import { FlowNodeTypes } from '../types'
import type {
  FlowRunEvent,
  NodeChunkCreatedEvent,
  NodeExecutedEvent,
  NodeUpdatedEvent,
  SystemChunkCreatedEvent,
} from './types'
import { highlightNodeAndEdges } from './utils'

interface AsyncAgentMeta {
  from: string
  to: string
  channelName?: Channels
  chatChannel?: string
}

export enum RunType {
  Playground = 'playground',
  FlowRunLogs = 'flow-run-logs',
}

// Flow Run Store Types
interface FlowRunState {
  runType?: RunType
  currentRunId: string | null
  events: FlowRunEvent[]
  // Last node will be the current node
  executionHistory: NodeExecutedEvent[]
  panelState: 'normal' | 'minimized' | 'maximized'
  totalResponseCount: number
  isFlowRunning: boolean
  asyncAgentMeta: AsyncAgentMeta
}

interface FlowRunActions {
  setCurrentRunId: (runId: string | null, runType?: RunType) => void
  setRunType: (runType: RunType) => void
  addEvent: (event: FlowRunEvent) => void
  setExecutionHistory: (node: NodeExecutedEvent) => void
  clearEvents: () => void
  reset: () => void
  setPanelState: (state: 'normal' | 'minimized' | 'maximized') => void
  setIsFlowRunning: (running: boolean) => void
  setAsyncAgentMeta: (meta: AsyncAgentMeta) => void
  generateExecutionHistoryFromEvents: (events: FlowRunEvent[]) => void
}

const initialState: FlowRunState = {
  currentRunId: null,
  panelState: 'maximized',
  runType: undefined,
  events: [],
  executionHistory: [],
  totalResponseCount: 0,
  isFlowRunning: false,
  asyncAgentMeta: { from: '', to: '', channelName: undefined },
}

type ChunkVariables = NonNullable<NodeChunkCreatedEvent['node_config']['variables']>

const aggregateChunkVariables = (
  acc: Record<string, unknown> | undefined,
  variables: ChunkVariables,
): Record<string, unknown> => {
  const result: Record<string, unknown> = acc ? { ...acc } : {}
  if (Array.isArray(variables)) {
    variables.forEach((v) => {
      result[v.variable_name] = v
    })
  } else {
    Object.entries(variables).forEach(([key, value]) => {
      result[key] = value
    })
  }
  return result
}

// Helper functions to reduce repetition
const createBaseNodeExecutedEvent = (event: FlowRunEvent): NodeExecutedEvent => ({
  chunks: [],
  current_node: '',
  current_node_uuid: event.node_uuid,
  current_node_type: null,
  chosen_path: '',
  previous_node: '',
  previous_node_uuid: '',
  event_name: 'NODE_EXECUTED',
  flow_run_uuid: event.flow_run_uuid,
  node_uuid: event.node_uuid,
  node_run_uuid: event.node_run_uuid,
  auth_id: event.auth_id,
  timestamp: event.timestamp,
  isBaseNode: true, // Flag to identify base nodes created from orphaned chunks
})

const getNodeKey = (event: FlowRunEvent) => {
  return 'node_run_uuid' in event && event.node_run_uuid ? 'node_run_uuid' : 'node_uuid'
}

/**
 * Finds a node execution by its unique node_run_uuid
 *
 * Since node_run_uuid is unique for each execution, this provides accurate
 * lookup without timestamp ambiguity, making it ideal for cyclic flows.
 *
 * @param executionHistory - Array of executed nodes
 * @param nodeRunUuid - Unique run UUID of the node execution to find
 * @returns Object containing the node and its index, or null if not found
 */
const findNodeByNodeRunUuid = (
  executionHistory: NodeExecutedEvent[],
  nodeRunUuid: string,
): {
  node: NodeExecutedEvent | null
  nodeIndex: number
} => {
  const nodeIndex = executionHistory.findIndex((node) => node.node_run_uuid === nodeRunUuid)
  return {
    node: nodeIndex !== -1 ? executionHistory[nodeIndex] : null,
    nodeIndex,
  }
}

const updateOrCreateNode = (
  state: FlowRunState,
  event: FlowRunEvent,
  updateFn: (node: NodeExecutedEvent) => void,
  createFn: () => NodeExecutedEvent,
) => {
  const { node, nodeIndex } = findNodeByNodeRunUuid(state.executionHistory, event.node_run_uuid)

  if (node) {
    updateFn(node)
    if (nodeIndex !== -1) {
      state.executionHistory[nodeIndex] = node
    }
  } else {
    state.executionHistory.push(createFn())
  }
}

/**
 * Handles NODE_EXECUTED events using node_run_uuid for accurate tracking
 *
 * Logic:
 * - If node_run_uuid doesn't exist → Add new entry to execution history
 * - If node_run_uuid exists and is a base node → Merge real NODE_EXECUTED data into placeholder
 * - If node_run_uuid exists and is not a base node → Ignore duplicate
 *
 * @param state - Current flow run state
 * @param event - NODE_EXECUTED event to process
 */
const handleNodeExecutedEvent = (state: FlowRunState, event: NodeExecutedEvent) => {
  const existingNodeIndex = state.executionHistory.findIndex(
    (node) => node.node_run_uuid === event.node_run_uuid,
  )

  if (existingNodeIndex !== -1) {
    const existingNode = state.executionHistory[existingNodeIndex]

    if (existingNode.isBaseNode) {
      // Base node exists from orphaned chunks - merge real NODE_EXECUTED data
      merge(existingNode, event)
    }
    // If not a base node, it's a duplicate - ignore it
  } else {
    // No node with this node_run_uuid exists - push new entry
    state.executionHistory.push(event)
  }
}

// Store everything related to flow runs
export const useFlowRunStore = createStore<FlowRunState & FlowRunActions>('playground', (set) => ({
  ...initialState,
  // Actions
  setCurrentRunId: (runId, runType) => set({ currentRunId: runId, runType }),
  setRunType: (runType) => set({ runType }),
  /**
   * Processes a single flow event in real-time during flow execution
   *
   * Key Processing Logic (mirrors generateExecutionHistoryFromEvents):
   * 1. **Flow State Check**: Only processes events when flow is actively running
   * 2. **Event Type Processing**:
   *    - NODE_EXECUTED: Creates or updates node entries in execution history
   *    - NODE_CHUNK_CREATED/SYSTEM_CHUNK_CREATED: Attaches streaming chunks to nodes
   *    - NODE_UPDATED: Merges configuration updates into existing nodes
   *    - FLOW_EXECUTION_COMPLETE: Adds synthetic end node to mark completion
   * 3. **Configuration Merging**: Combines node configs from chunks and updates
   * 4. **Response Counting**: Tracks assistant messages for analytics
   * 5. **Orphan Handling**: Creates base nodes for chunks without parent NODE_EXECUTED events
   * 6. **node_run_uuid Grouping**: Uses unique node_run_uuid for accurate cyclic flow handling
   */
  addEvent: (event) => {
    set((state) => {
      if (!state.isFlowRunning) return

      // Turn detection metrics are diagnostic-only and must never reach the transcript UI.
      if (event.event_name === 'SYSTEM_CHUNK_CREATED' && event.type === 'TURN_DETECTION_METRIC') {
        return
      }

      if (event.event_name === 'NODE_EXECUTED') {
        handleNodeExecutedEvent(state, event)

        // For api_request flows, asyncAgentMeta is not set during initialization.
        // Populate it from the first node that has channel, from, and to info (e.g., preset node),
        // reversing from/to since the reply goes in the opposite direction.
        // Only hydrate when all three values are available to avoid freezing meta with empty fields.
        const derivedFrom = typeof event.node_config?.to === 'string' ? event.node_config.to : ''
        const derivedTo = typeof event.node_config?.from === 'string' ? event.node_config.from : ''
        const derivedChannel = event.node_config?.channel as Channels | undefined
        const shouldHydrateMeta =
          !!derivedChannel &&
          !!derivedFrom &&
          !!derivedTo &&
          (!state.asyncAgentMeta.channelName ||
            !state.asyncAgentMeta.from ||
            !state.asyncAgentMeta.to)

        if (shouldHydrateMeta) {
          state.asyncAgentMeta = {
            ...state.asyncAgentMeta,
            from: derivedFrom,
            to: derivedTo,
            channelName: derivedChannel,
          }
        }
      }

      if (
        event.event_name === 'NODE_CHUNK_CREATED' ||
        event.event_name === 'SYSTEM_CHUNK_CREATED'
      ) {
        if (!isUUID(event.node_uuid) && !isUUID(event.node_run_uuid)) return

        updateOrCreateNode(
          state,
          event,
          (node) => {
            const isEmptyAssistantWithVars =
              event.event_name === 'NODE_CHUNK_CREATED' &&
              event.sender === 'assistant' &&
              !event.text &&
              // A chat card has no text either; its content is in attributes, so keep it.
              // Other channels keep the old behaviour and still drop their carrier rows.
              !(event.node_config?.channel === 'chat' && event.attributes) &&
              event.node_config?.variables

            if (!isEmptyAssistantWithVars) {
              if (node.chunks) node.chunks.push(event)
              else node.chunks = [event]
            }

            if (event.event_name === 'NODE_CHUNK_CREATED') {
              if (event.node_config) {
                node.node_config = merge(node.node_config, event.node_config)
                event.node_config = cloneDeep(node.node_config)
              }
              if (event.sender === 'assistant') state.totalResponseCount += 1
            }
          },
          () => ({ ...createBaseNodeExecutedEvent(event), chunks: [event] }),
        )
      }

      if (event.event_name === 'NODE_UPDATED') {
        updateOrCreateNode(
          state,
          event,
          (node) => {
            node.node_config = merge(node.node_config, event.node_config)
          },
          () => ({
            ...createBaseNodeExecutedEvent(event),
            node_config: event.node_config as unknown as undefined,
          }),
        )
      }

      if (event.event_name === 'FLOW_EXECUTION_COMPLETE') {
        state.executionHistory.push({
          ...event,
          event_name: 'NODE_EXECUTED',
          current_node: 'Test End',
          current_node_type: FlowNodeTypes.end_conversation,
          chunks: [],
        } as NodeExecutedEvent)
      }

      state.events.push(event)
    })
  },
  setExecutionHistory: (node) =>
    set((state) => ({ executionHistory: [...state.executionHistory, node] })),
  clearEvents: () => set({ events: [] }),
  reset: () => {
    // clear all highlighting
    highlightNodeAndEdges([], [])
    set({ ...initialState })
  },
  setPanelState: (state) => set({ panelState: state }),
  setIsFlowRunning: (running) => set({ isFlowRunning: running }),
  setAsyncAgentMeta: (meta) => set({ asyncAgentMeta: meta }),
  /**
   * Key Processing Logic:
   * 1. **State Reset**: Clears existing execution history, events, and response count
   * 2. **Chronological Ordering**: Sorts events by timestamp to ensure proper sequence
   * 3. **Event Aggregation**: Groups related events by node_uuid using an index map
   * 4. **Event Type Processing**:
   *    - NODE_EXECUTED: Creates new node entries in execution history
   *    - NODE_CHUNK_CREATED/SYSTEM_CHUNK_CREATED: Attaches streaming chunks to nodes
   *    - NODE_UPDATED: Merges configuration updates into existing nodes
   *    - FLOW_EXECUTION_COMPLETE: Adds synthetic end node to mark completion
   * 5. **Configuration Merging**: Combines node configs from chunks and updates
   * 6. **Response Counting**: Tracks assistant messages for analytics
   * 7. **Orphan Handling**: Creates base nodes for chunks without parent NODE_EXECUTED events
   */
  generateExecutionHistoryFromEvents: (events) => {
    set((state) => {
      // Reset execution history and events to start fresh
      state.executionHistory = []
      state.events = []
      state.totalResponseCount = 0

      // Turn detection metrics are diagnostic-only and must never reach the transcript UI.
      events = events.filter(
        (event) =>
          !(event.event_name === 'SYSTEM_CHUNK_CREATED' && event.type === 'TURN_DETECTION_METRIC'),
      )

      const { nodeExecutedEvents, nodeUpdatesByNodeRun, chunksByNodeRun, flowCompleteEvents } =
        events.reduce(
          (acc, event) => {
            const nodeKey = event[getNodeKey(event)]
            switch (event.event_name) {
              case 'NODE_EXECUTED':
                acc.nodeExecutedEvents.push(cloneDeep(event) as NodeExecutedEvent)
                break
              case 'NODE_CHUNK_CREATED':
              case 'SYSTEM_CHUNK_CREATED':
                if (!event.event_name)
                  (event as SystemChunkCreatedEvent).event_name = 'SYSTEM_CHUNK_CREATED'

                if (acc.chunksByNodeRun[nodeKey]) {
                  acc.chunksByNodeRun[nodeKey].push(cloneDeep(event))
                } else {
                  acc.chunksByNodeRun[nodeKey] = [cloneDeep(event)]
                }

                break
              case 'NODE_UPDATED':
                if (acc.nodeUpdatesByNodeRun[nodeKey]) {
                  acc.nodeUpdatesByNodeRun[nodeKey].push(event)
                } else {
                  acc.nodeUpdatesByNodeRun[nodeKey] = [event]
                }
                break
              case 'FLOW_EXECUTION_COMPLETE':
                acc.flowCompleteEvents.push(event)
                break
              default:
                break
            }

            return acc
          },
          {
            nodeExecutedEvents: [] as NodeExecutedEvent[],
            chunksByNodeRun: {} as Record<string, typeof events>,
            nodeUpdatesByNodeRun: {} as Record<string, typeof events>,
            flowCompleteEvents: [] as typeof events,
          },
        )

      nodeExecutedEvents.forEach((nodeEvent) => {
        const nodeKey = nodeEvent[getNodeKey(nodeEvent)]
        const nodeChunks = chunksByNodeRun[nodeKey] || []
        const nodeUpdates = nodeUpdatesByNodeRun[nodeKey] || []

        let aggregatedNodeConfig = nodeEvent.node_config || {}

        nodeUpdates
          .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
          .forEach((updateEvent) => {
            if ((updateEvent as NodeUpdatedEvent).node_config) {
              aggregatedNodeConfig = merge(
                aggregatedNodeConfig,
                (updateEvent as NodeUpdatedEvent).node_config,
              )
            }
          })

        nodeChunks
          .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
          .forEach((chunk) => {
            if (chunk.event_name === 'NODE_CHUNK_CREATED') {
              if (chunk.sender === 'assistant') state.totalResponseCount += 1
              if (chunk.node_config) {
                const { variables, ...configWithoutVariables } = chunk.node_config
                aggregatedNodeConfig = merge(aggregatedNodeConfig, configWithoutVariables)
                chunk.node_config = { ...aggregatedNodeConfig, variables }
              }
            }
          })

        nodeEvent.node_config = aggregatedNodeConfig

        // LiveKit emits variables on a separate empty assistant chunk that is dropped below.
        // Collate only those dropped chunks' variables so the node card can show a combined
        // summary. Pipecat carries variables on surviving text chunks, so it produces no
        // dropped variable-only chunks here and its behaviour is unchanged.
        let aggregatedVariables: Record<string, unknown> | undefined

        nodeEvent.chunks = nodeChunks.filter((chunk) => {
          if (
            chunk.event_name === 'NODE_CHUNK_CREATED' &&
            chunk.sender === 'assistant' &&
            !chunk.text &&
            // A chat card has no text either; its content is in attributes, so keep it.
            // Other channels keep the old behaviour and still drop their carrier rows.
            !(chunk.node_config?.channel === 'chat' && chunk.attributes) &&
            chunk.node_config?.variables
          ) {
            aggregatedVariables = aggregateChunkVariables(
              aggregatedVariables,
              chunk.node_config.variables,
            )
            return false
          }
          return true
        }) as NodeChunkCreatedEvent[]

        if (aggregatedVariables) nodeEvent.aggregated_variables = aggregatedVariables
        state.executionHistory.push(nodeEvent)
      })

      flowCompleteEvents.forEach((completeEvent) => {
        state.executionHistory.push({
          ...completeEvent,
          event_name: 'NODE_EXECUTED',
          current_node: 'Test End',
          current_node_type: FlowNodeTypes.end_conversation,
          chunks: [],
        } as NodeExecutedEvent)
      })

      state.executionHistory.sort((a, b) => a.timestamp.localeCompare(b.timestamp))

      // Store all events for reference
      state.events = [...events].sort((a, b) => a.timestamp.localeCompare(b.timestamp))
    })
  },
}))
